import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, catchError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GenerateClassificationService {
  private apiUrl = environment.backendApiUrl;
  public metadataSubject: any = new BehaviorSubject({});
  public originalMetadataSubject: any = new BehaviorSubject({});
  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private router: Router
  ) {}

  private getSessionHeader(): { headers: HttpHeaders } {
    const sessionId = sessionStorage.getItem('Session_Id');
    return {
      headers: new HttpHeaders({
        'X-Session-ID': `${sessionId || ''}`,
      }),
    };
  }

  getDatabase(): Observable<any[]> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any[]>(
        this.apiUrl + '/snowflake-metadata/databases',
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getSchema(database: string): Observable<any[]> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any[]>(
        this.apiUrl + '/snowflake-metadata/schemas/' + database,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getTable(database: string, schema: string): Observable<any[]> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any[]>(
        this.apiUrl +
          '/snowflake-metadata/tables/' +
          database +
          '/' +
          schema,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getMetadata(postData: any): Observable<any> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any>(
        this.apiUrl +
          '/snowflake-metadata/metadata/' +
          postData.database_name +
          '/' +
          postData.schema_name +
          '/' +
          postData.table_name +
          '?atlan=' +
          postData.isAtlan,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  saveMetadata(postData: any): Observable<any> {
    const sessionId = this.getSessionHeader();
    return this.http
      .post<any>(
        this.apiUrl + '/generate-classification/generate',
        postData,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  saveMetadataDescription(postData: any): Observable<any> {
    const sessionId = this.getSessionHeader();
    return this.http
      .post<any>(
        this.apiUrl + '/generate-classification/save',
        postData,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  regenerateMetadata(postData: any): Observable<any> {
    const sessionId = this.getSessionHeader();
    return this.http
      .post<any>(
        this.apiUrl + '/generate-classification/regenerate',
        postData,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  atlanBulkDownload(postData: any): Observable<any> {
    const sessionId = sessionStorage.getItem('Session_Id');
    const url = this.apiUrl + '/export/excel?create_template=false';

    const headers = new HttpHeaders({
      'Content-Type':
        postData instanceof FormData ? (undefined as any) : 'application/json',
      'X-Session-ID': `${sessionId || ''}`,
    });

    return this.http
      .post(url, postData, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  private handleApiError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.status === 0) {
      errorMessage = 'Network error: Please check your internet connection';
    } else if (error.status === 401) {
      errorMessage = 'Session expired. Please login again.';
      setTimeout(() => this.router.navigate(['/login']), 2000);
    } else if (error.status === 403) {
      errorMessage = 'You do not have permission for this action';
    } else if (error.status === 404) {
      errorMessage = 'Content not found';
    } else if (error.status === 500) {
      errorMessage = 'Server Busy: Please try again';
    } else if (error.status >= 500) {
      errorMessage = 'Server error: Please try again later';
    }

    this.toastr.error(errorMessage, 'Error');

    return throwError(() => error);
  }
}
